<template>
  <div direction="right" class="default-slider space-top-lg">
    <div class="slider-inner space-top-lg" style="width:55%; z-index: 9;" v-if="!isMetaDataEmpty">
      <div class="slider-header">
        <span>
          {{ selectedEntity.entity_id }}
        </span>
        <div v-if="!hasMultipleEntries && showRetrigger && selectedEntity.transaction_type === transactionTypes.OUTBOUND">
          <Button
            type="secondary"
            class="btn"
            @click="add(selectedEntity)"
          >{{$t('INDENT_RETRIGGER')}}</Button>
        </div>
      </div>
      <div class="slider-body p-0">

        <!-- SINGLE ENTRY: existing flat layout -->
        <template v-if="!hasMultipleEntries">
          <ElTabs class="tab-default tab-sm tab-bg no-bg">
            <Tab-pane :label="$t('INTEGRATION_DETAILS')">
              <div class="tab-content">
                <div class="flex-container panel-head align-top mb-0">
                  <div v-for="(item, index) in detailsList" :key="index">
                    <div v-for="(option, idx) in item" :key="idx" class="flex-item">
                      <template v-if="!option.hideWhenFeature || !$hasFeature(option.hideWhenFeature)">
                      <div v-if="option.dataType === 'date' && selectedEntity[option.value]">
                        <p>
                          {{ $t(option.tlabel) }} :
                          <strong>{{$moment(selectedEntity[option.value]).format("DD-MMM-YYYY")}}</strong>
                        </p>
                      </div>
                      <div v-if="option.dataType === 'string' && selectedEntity[option.value]">
                        <p>
                          {{ $t(option.tlabel) }} : <strong>{{selectedEntity[option.value]}}</strong>
                        </p>
                      </div>
                      </template>
                    </div>
                  </div>
                </div>
                <DestinationResponsesTable
                  v-if="$hasFeature('mitm_destination_responses') && selectedEntity.destination_responses && selectedEntity.destination_responses.length"
                  :responses="selectedEntity.destination_responses"
                />
              </div>
            </Tab-pane>
          </ElTabs>
          <ElTabs class="tab-default tab-sm tab-bg no-bg">
            <Tab-pane :label="$t('INTEGRATION_PAYLOAD')">
              <div class="tab-content">
                <Row>
                  <Col v-if="selectedEntity.transaction_type === transactionTypes.OUTBOUND" :span="10">
                    {{ $t('INTEGRATION_JSON_FILENAME') }} :
                  </Col>
                  <Col v-if="selectedEntity.transaction_type === transactionTypes.INBOUND" :span="10">
                    {{ $t('INTEGRATION_EXCEL_FILENAME') }} :
                  </Col>
                  <Col :span="4">
                    <div v-if="selectedEntity.req_s3_url">
                      <Tooltip :open-delay="100" effect="dark" placement="top-end">
                        <i class="action-icon" @click="getPayload()">
                          <a class='download-file'></a>
                          <svgicon v-if="selectedEntity.transaction_type === transactionTypes.OUTBOUND" name="icon-json" :original="true"/>
                          <svgicon v-if="selectedEntity.transaction_type === transactionTypes.INBOUND" name="icon-excel" :original="true"/>
                        </i>
                        <div slot="content" v-if="selectedEntity.transaction_type === transactionTypes.OUTBOUND">
                          {{ $t('INTEGRATION_DOWNLOAD_JSON') }}
                        </div>
                        <div slot="content" v-if="selectedEntity.transaction_type === transactionTypes.INBOUND">
                          {{ $t('INTEGRATION_DOWNLOAD_EXCEL') }}
                        </div>
                      </Tooltip>
                    </div>
                    <div v-else>
                      <strong>{{ $t('PAYLOAD_UNAVAILABLE_MSG') }}</strong>
                    </div>
                  </Col>
                </Row>
              </div>
            </Tab-pane>
          </ElTabs>
        </template>

        <!-- MULTIPLE ENTRIES: Accordion layout -->
        <template v-else>
          <ElCollapse v-model="activeAccordion" class="integration-accordion">
            <ElCollapseItem
              v-for="(entry, index) in selectedEntity.history"
              :key="entry.tms_transaction_id || index"
              :name="index"
            >
              <template slot="title">
                <div class="accordion-header">
                  <span class="accordion-timestamp">{{ entry.created_at }}</span>
                  <span class="accordion-status">
                    MITM: {{ entry.mitm_error_status || 'N/A' }} | ERP: {{ entry.erp_status_code || 'N/A' }}
                  </span>
                  <span v-if="index === 0" class="accordion-latest-tag">Latest</span>
                </div>
              </template>

              <div class="accordion-content">
                <!-- Retrigger — only on latest (index 0) -->
                <div v-if="index === 0 && showRetriggerForEntry(entry) && selectedEntity.transaction_type === transactionTypes.OUTBOUND" class="accordion-retrigger">
                  <Button type="secondary" class="btn" @click="add(entry)">
                    {{ $t('INDENT_RETRIGGER') }}
                  </Button>
                </div>

                <!-- Detail fields — reuse existing flex layout from original design -->
                <div class="flex-container panel-head align-top mb-0">
                  <div v-for="(item, dIdx) in detailsList" :key="dIdx">
                    <div v-for="(option, oIdx) in item" :key="oIdx" class="flex-item">
                      <template v-if="!option.hideWhenFeature || !$hasFeature(option.hideWhenFeature)">
                      <div v-if="option.dataType === 'date' && entry[option.value]">
                        <p>
                          {{ $t(option.tlabel) }} :
                          <strong>{{ $moment(entry[option.value]).format("DD-MMM-YYYY") }}</strong>
                        </p>
                      </div>
                      <div v-if="option.dataType === 'string' && entry[option.value]">
                        <p>
                          {{ $t(option.tlabel) }} : <strong>{{ entry[option.value] }}</strong>
                        </p>
                      </div>
                      </template>
                    </div>
                  </div>
                </div>

                <!-- Payload download per entry -->
                <div class="accordion-payload">
                  <span class="detail-label">{{ $t('INTEGRATION_JSON_FILENAME') }}:</span>
                  <span v-if="entry.req_s3_url" class="pointer payload-link" @click="getPayloadForEntry(entry)">
                    <svgicon name="icon-json" :original="true"/>
                    Download
                  </span>
                  <strong v-else>{{ $t('PAYLOAD_UNAVAILABLE_MSG') }}</strong>
                </div>

                <DestinationResponsesTable
                  v-if="$hasFeature('mitm_destination_responses') && entry.destination_responses && entry.destination_responses.length"
                  :responses="entry.destination_responses"
                />
              </div>
            </ElCollapseItem>
          </ElCollapse>
        </template>

      </div>
    </div>
  </div>
</template>

<script>
import { Collapse as ElCollapse, CollapseItem as ElCollapseItem } from 'element-ui';
import { detailsList, transactionTypes } from '@/constants/integrations';
import DestinationResponsesTable from './DestinationResponsesTable';

export default {
  name: 'entityDetails',
  components: { ElCollapse, ElCollapseItem, DestinationResponsesTable },
  props: {
    selectedEntity: {
      type: Object,
    },
    columnType: {
      type: Number,
    },
    columnName: {
      type: String,
    },
  },
  data() {
    return {
      detailsList,
      transactionTypes,
      activeAccordion: [0],
    };
  },
  computed: {
    isMetaDataEmpty() {
      if (this.selectedEntity && this.selectedEntity.api_name) return false;
      return true;
    },
    hasMultipleEntries() {
      return this.selectedEntity?.history?.length > 1;
    },
    showRetrigger() {
      return (this.$hasFeature('integration_success_retrigger') ||
        this.selectedEntity.erp_retry_requested ||
        (this.selectedEntity.erp_status_code >= 400 ||
        this.selectedEntity.mitm_error_status >= 400 ||
        this.selectedEntity.mitm_async_error >= 400 ||
        this.selectedEntity.client_status_code >= 400));
    },
  },
  watch: {
    selectedEntity() {
      this.activeAccordion = [0];
    },
  },
  methods: {
    showRetriggerForEntry(entry) {
      return (this.$hasFeature('integration_success_retrigger') ||
        entry.erp_retry_requested ||
        (entry.erp_status_code >= 400 ||
        entry.mitm_error_status >= 400 ||
        entry.mitm_async_error >= 400 ||
        entry.client_status_code >= 400));
    },
    async getPayload() {
      if (this.selectedEntity) {
        const tmsTransactionId = this.selectedEntity.tms_transaction_id;
        const result = await this.$store.dispatch('order/getMitmPayload', { tmsTransactionId });
        if (result && result.error) {
          this.$errorMessage({ message: result.error });
        }
      }
    },
    async getPayloadForEntry(entry) {
      if (entry && entry.tms_transaction_id) {
        const result = await this.$store.dispatch('order/getMitmPayload', {
          tmsTransactionId: entry.tms_transaction_id,
        });
        if (result && result.error) {
          this.$errorMessage({ message: result.error });
        }
      }
    },
    async add(entry) {
      const target = entry || this.selectedEntity;
      if (target) {
        const data = {
          entity_id: target.entity_id,
          api_name: target.api_name,
          log_object_id: target.log_object_id,
        };
        const result = await this.$store.dispatch('order/retryRequest', { data });
        if (result.status === 204) {
          this.$successMessage({ message: 'Retriggered successfully. Please check after sometime' });
        } else {
          this.$errorMessage({ message: result.error || 'Retrigger failed. Please try again later' });
        }
      }
    },
  },
};
</script>

<style scoped>
.default-slider .slider-inner.space-top-lg {
  top: 248px !important;
}

/* Accordion container */
.integration-accordion {
  border: none;
  padding: 0 16px;
}
.integration-accordion >>> .el-collapse-item__header {
  height: 48px;
  line-height: 48px;
  border-bottom: 1px solid #EBEEF5;
  padding: 0 8px;
  background: #FAFAFA;
}
.integration-accordion >>> .el-collapse-item__wrap {
  border-bottom: 1px solid #EBEEF5;
}
.integration-accordion >>> .el-collapse-item__content {
  padding: 0;
}

/* Accordion header */
.accordion-header {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding-right: 8px;
}
.accordion-timestamp {
  font-weight: 600;
  font-size: 13px;
  color: #303133;
  white-space: nowrap;
}
.accordion-status {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
}
.accordion-latest-tag {
  background: #67C23A;
  color: #fff;
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: auto;
  white-space: nowrap;
  line-height: 16px;
  height: 18px;
  display: inline-flex;
  align-items: center;
}

/* Accordion body content */
.accordion-content {
  padding: 16px;
}
.accordion-retrigger {
  margin-bottom: 16px;
}

/* Payload section */
.accordion-payload {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #EBEEF5;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.payload-link {
  color: #409EFF;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}
.payload-link:hover {
  text-decoration: underline;
}
</style>
